import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Search, X, Plus, Filter, LayoutGrid, List,
  Trash2, CheckCircle2, Upload, Eye, EyeOff,
  Loader2, Copy, Edit3, AlertTriangle, Package,
  TrendingUp, TrendingDown, RefreshCw, Minus,
  ChevronDown, BarChart2, Archive, Truck, Clock,
  Image as ImageIcon, ShoppingCart, Layers
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import axios from 'axios';
import { baseurl, imgurl } from '../../baseurl';

/* ═══════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════ */
const STOCK_UNITS = [
  { id: 'kg',    label: 'kg'    },
  { id: 'g',     label: 'g'     },
  { id: 'l',     label: 'L'     },
  { id: 'ml',    label: 'ml'    },
  { id: 'pcs',   label: 'pcs'   },
  { id: 'dozen', label: 'dozen' },
  { id: 'box',   label: 'box'   },
  { id: 'pack',  label: 'pack'  },
  { id: 'bottle',label: 'bottle'},
  { id: 'can',   label: 'can'   },
  { id: 'bag',   label: 'bag'   },
  { id: 'tray',  label: 'tray'  },
];

const STOCK_CATEGORIES = [
  { id: 'all',         name: 'All',              icon: '📦' },
  { id: 'vegetables',  name: 'Vegetables',        icon: '🥦' },
  { id: 'fruits',      name: 'Fruits',            icon: '🍎' },
  { id: 'dairy',       name: 'Dairy',             icon: '🥛' },
  { id: 'meat',        name: 'Meat & Poultry',    icon: '🥩' },
  { id: 'seafood',     name: 'Seafood',           icon: '🦐' },
  { id: 'grains',      name: 'Grains & Flour',    icon: '🌾' },
  { id: 'spices',      name: 'Spices & Herbs',    icon: '🌶️' },
  { id: 'oils',        name: 'Oils & Fats',       icon: '🫙' },
  { id: 'beverages',   name: 'Beverages',         icon: '🧃' },
  { id: 'packaging',   name: 'Packaging',         icon: '📫' },
  { id: 'cleaning',    name: 'Cleaning',          icon: '🧹' },
  { id: 'other',       name: 'Other',             icon: '🗂️' },
];

const ADJUST_TYPES = [
  { id: 'restock',  label: 'Restock',      icon: <TrendingUp  size={13} />, color: '#22c55e' },
  { id: 'use',      label: 'Used',         icon: <TrendingDown size={13}/>, color: '#f59e0b' },
  { id: 'waste',    label: 'Waste/Damage', icon: <Trash2      size={13} />, color: '#ef4444' },
  { id: 'return',   label: 'Return',       icon: <RefreshCw   size={13} />, color: '#3b82f6' },
  { id: 'transfer', label: 'Transfer',     icon: <Layers      size={13} />, color: '#8b5cf6' },
];

const SORT_OPTIONS = [
  { id: 'default',       label: 'Default'       },
  { id: 'name_asc',      label: 'A → Z'         },
  { id: 'qty_asc',       label: 'Qty ↑'         },
  { id: 'qty_desc',      label: 'Qty ↓'         },
  { id: 'low_stock',     label: 'Low Stock First'},
  { id: 'expiry',        label: 'Expiry Soon'   },
];

const BLANK_FORM = {
  name: '', category: '', unit: 'kg',
  current_qty: '', min_qty: '', max_qty: '',
  cost_per_unit: '', supplier_name: '', supplier_contact: '',
  expiry_date: '', notes: '',
  image_url: '', image_file: null, image_preview: '',
  is_active: true,
};

const BLANK_ADJUST = {
  type: 'restock', qty: '', note: '', cost_per_unit: '',
};

/* ═══════════════════════════════════════
   AXIOS HELPER
═══════════════════════════════════════ */
const api = () => {
  const token = localStorage.getItem('restaurant_token');
  return axios.create({
    baseURL: `${baseurl}`,
    headers: { Authorization: `Bearer ${token}` },
  });
};

/* ═══════════════════════════════════════
   TOAST HOOK
═══════════════════════════════════════ */
function useToast() {
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });
  const fire = useCallback((msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
  }, []);
  return { toast, fire };
}

/* ═══════════════════════════════════════
   STOCK STATUS HELPER
═══════════════════════════════════════ */
function getStockStatus(item) {
  const qty = parseFloat(item.current_qty) || 0;
  const min = parseFloat(item.min_qty)     || 0;
  if (qty <= 0)        return { label: 'Out of Stock',  color: '#ef4444', bg: 'rgba(239,68,68,0.10)',  dot: '#ef4444' };
  if (qty <= min)      return { label: 'Low Stock',     color: '#f59e0b', bg: 'rgba(245,158,11,0.10)', dot: '#f59e0b' };
  return               { label: 'In Stock',             color: '#22c55e', bg: 'rgba(34,197,94,0.10)',  dot: '#22c55e' };
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
function Stock() {
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [activeCat,    setActiveCat]    = useState('all');
  const [search,       setSearch]       = useState('');
  const [sortBy,       setSortBy]       = useState('default');
  const [viewMode,     setViewMode]     = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all'|'low'|'out'

  // Panel: null | 'create' | 'edit' | 'adjust' | 'history'
  const [panel,        setPanel]        = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [panelTab,     setPanelTab]     = useState('info');

  const [form,         setForm]         = useState(BLANK_FORM);
  const [adjustForm,   setAdjustForm]   = useState(BLANK_ADJUST);
  const [history,      setHistory]      = useState([]);
  const [histLoading,  setHistLoading]  = useState(false);

  const { toast, fire } = useToast();
  const fileInputRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  /* ── Load ── */
  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api().get('/stockitems').catch(() => ({ data: { items: [] } }));
      const raw = res.data.items || [];
      setItems(raw.map(normalizeItem));
    } catch (err) {
      fire('Failed to load stock', 'error');
    } finally {
      setLoading(false);
    }
  };

  const normalizeItem = (p) => ({
    ...p,
    current_qty:   parseFloat(p.current_qty)   || 0,
    min_qty:       parseFloat(p.min_qty)        || 0,
    max_qty:       parseFloat(p.max_qty)        || 0,
    cost_per_unit: parseFloat(p.cost_per_unit)  || 0,
    is_active:     p.is_active !== undefined ? Boolean(p.is_active) : true,
  });

  /* ── Filter + Sort ── */
  const filtered = useMemo(() => {
    let list = items.filter(p => {
      const catOk    = activeCat === 'all' || p.category === activeCat;
      const searchOk = p.name.toLowerCase().includes(search.toLowerCase()) ||
                       (p.supplier_name || '').toLowerCase().includes(search.toLowerCase());
      const status   = getStockStatus(p);
      const statusOk = filterStatus === 'all'
        ? true
        : filterStatus === 'low' ? status.label === 'Low Stock'
        : filterStatus === 'out' ? status.label === 'Out of Stock'
        : true;
      return catOk && searchOk && statusOk;
    });

    switch (sortBy) {
      case 'name_asc':  list = [...list].sort((a,b) => a.name.localeCompare(b.name)); break;
      case 'qty_asc':   list = [...list].sort((a,b) => a.current_qty - b.current_qty); break;
      case 'qty_desc':  list = [...list].sort((a,b) => b.current_qty - a.current_qty); break;
      case 'low_stock': list = [...list].sort((a,b) => {
        const ra = a.min_qty > 0 ? a.current_qty / a.min_qty : 999;
        const rb = b.min_qty > 0 ? b.current_qty / b.min_qty : 999;
        return ra - rb;
      }); break;
      case 'expiry': list = [...list].sort((a,b) => {
        if (!a.expiry_date) return 1;
        if (!b.expiry_date) return -1;
        return new Date(a.expiry_date) - new Date(b.expiry_date);
      }); break;
      default: break;
    }
    return list;
  }, [items, activeCat, search, sortBy, filterStatus]);

  /* Summary stats */
  const stats = useMemo(() => ({
    total:    items.length,
    low:      items.filter(p => { const s = getStockStatus(p); return s.label === 'Low Stock'; }).length,
    out:      items.filter(p => { const s = getStockStatus(p); return s.label === 'Out of Stock'; }).length,
    value:    items.reduce((sum, p) => sum + (p.current_qty * p.cost_per_unit), 0),
  }), [items]);

  /* ── Panel helpers ── */
  const openCreate = () => {
    setForm({ ...BLANK_FORM });
    setSelectedItem(null);
    setPanel('create');
    setPanelTab('info');
  };

  const openEdit = (item) => {
    setForm({
      name:             item.name             || '',
      category:         item.category         || '',
      unit:             item.unit             || 'kg',
      current_qty:      item.current_qty      ?? '',
      min_qty:          item.min_qty          ?? '',
      max_qty:          item.max_qty          ?? '',
      cost_per_unit:    item.cost_per_unit    ?? '',
      supplier_name:    item.supplier_name    || '',
      supplier_contact: item.supplier_contact || '',
      expiry_date:      item.expiry_date      ? item.expiry_date.split('T')[0] : '',
      notes:            item.notes            || '',
      image_url:        item.image_url        || '',
      image_file:       null,
      image_preview:    item.image_url        ? `${imgurl}${item.image_url}` : '',
      is_active:        item.is_active        !== false,
    });
    setSelectedItem(item);
    setPanel('edit');
    setPanelTab('info');
  };

  const openAdjust = (item) => {
    setSelectedItem(item);
    setAdjustForm({ ...BLANK_ADJUST, cost_per_unit: item.cost_per_unit || '' });
    setPanel('adjust');
  };

  const openHistory = async (item) => {
    setSelectedItem(item);
    setPanel('history');
    setHistLoading(true);
    try {
      const res = await api().get(`/stockitems/${item.id}/history`);
      setHistory(res.data.history || []);
    } catch { fire('Failed to load history', 'error'); }
    finally  { setHistLoading(false); }
  };

  const closePanel = () => {
    if (form.image_preview?.startsWith('blob:')) URL.revokeObjectURL(form.image_preview);
    setPanel(null);
    setSelectedItem(null);
    setUploadProgress(0);
  };

  const setF  = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAF = (k, v) => setAdjustForm(f => ({ ...f, [k]: v }));

  /* ── Image handlers ── */
  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/'))       { fire('Select a valid image', 'error'); return; }
    if (file.size > 5 * 1024 * 1024)           { fire('Image must be under 5 MB', 'error'); return; }
    if (form.image_preview?.startsWith('blob:')) URL.revokeObjectURL(form.image_preview);
    setForm(f => ({ ...f, image_file: file, image_preview: URL.createObjectURL(file), image_url: '' }));
  };

  const handleImageUrlChange = (url) => {
    if (form.image_preview?.startsWith('blob:')) URL.revokeObjectURL(form.image_preview);
    setForm(f => ({ ...f, image_url: url, image_file: null, image_preview: url }));
  };

  const clearImage = () => {
    if (form.image_preview?.startsWith('blob:')) URL.revokeObjectURL(form.image_preview);
    setForm(f => ({ ...f, image_url: '', image_file: null, image_preview: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* ── Save item ── */
  const handleSave = async () => {
    if (!form.name.trim())   { fire('Name is required', 'error'); return; }
    if (!form.unit)          { fire('Unit is required', 'error'); return; }
    setSaving(true);
    setUploadProgress(0);
    try {
      let finalImageUrl = form.image_url;

      if (form.image_file) {
        const fd = new FormData();
        fd.append('stock_image', form.image_file);
        const upRes = await api().post('/upload-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: e => setUploadProgress(Math.round(e.loaded * 100 / e.total)),
        });
        finalImageUrl = upRes.data.image_url;
      }

      const payload = {
        name:             form.name.trim(),
        category:         form.category,
        unit:             form.unit,
        current_qty:      parseFloat(form.current_qty)   || 0,
        min_qty:          parseFloat(form.min_qty)        || 0,
        max_qty:          parseFloat(form.max_qty)        || 0,
        cost_per_unit:    parseFloat(form.cost_per_unit)  || 0,
        supplier_name:    form.supplier_name,
        supplier_contact: form.supplier_contact,
        expiry_date:      form.expiry_date || null,
        notes:            form.notes,
        is_active:        form.is_active ? 1 : 0,
        ...(finalImageUrl ? { image_url: finalImageUrl } : {}),
      };

      if (panel === 'create') {
        const res  = await api().post('/stockitems', payload);
        const newI = res.data.item || { ...payload, id: Date.now() };
        setItems(prev => [normalizeItem(newI), ...prev]);
        fire(`"${form.name}" added to stock`);
      } else {
        await api().put(`/stockitems/${selectedItem.id}`, payload);
        setItems(prev => prev.map(p =>
          p.id === selectedItem.id ? normalizeItem({ ...p, ...payload }) : p
        ));
        fire(`"${form.name}" updated`);
      }
      closePanel();
    } catch (err) {
      fire(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  /* ── Stock adjustment ── */
  const handleAdjust = async () => {
    if (!adjustForm.qty || parseFloat(adjustForm.qty) <= 0) {
      fire('Enter a valid quantity', 'error'); return;
    }
    setSaving(true);
    try {
      const res = await api().post(`/stockitems/${selectedItem.id}/adjust`, {
        type:          adjustForm.type,
        qty:           parseFloat(adjustForm.qty),
        note:          adjustForm.note,
        cost_per_unit: adjustForm.cost_per_unit ? parseFloat(adjustForm.cost_per_unit) : null,
      });
      const updated = res.data.item;
      setItems(prev => prev.map(p => p.id === selectedItem.id ? normalizeItem(updated || { ...p }) : p));
      fire(`Stock ${adjustForm.type} recorded`);
      closePanel();
    } catch (err) {
      fire(err.response?.data?.message || 'Adjustment failed', 'error');
    } finally { setSaving(false); }
  };

  /* ── Delete ── */
  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from stock?`)) return;
    try {
      await api().delete(`/stockitems/${item.id}`);
      setItems(prev => prev.filter(p => p.id !== item.id));
      if (selectedItem?.id === item.id) closePanel();
      fire(`"${item.name}" deleted`);
    } catch { fire('Failed to delete', 'error'); }
  };

  /* ── Toggle active ── */
  const handleToggleActive = async (item, e) => {
    e.stopPropagation();
    const next = !item.is_active;
    try {
      await api().put(`/stockitems/${item.id}`, { is_active: next ? 1 : 0 });
      setItems(prev => prev.map(p => p.id === item.id ? { ...p, is_active: next } : p));
      fire(next ? `"${item.name}" activated` : `"${item.name}" deactivated`);
    } catch { fire('Failed to update', 'error'); }
  };

  /* ── Expiry helpers ── */
  const expiryWarning = (dateStr) => {
    if (!dateStr) return null;
    const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (days < 0)  return { label: 'Expired',     color: '#ef4444' };
    if (days <= 3) return { label: `${days}d left`, color: '#ef4444' };
    if (days <= 7) return { label: `${days}d left`, color: '#f59e0b' };
    return null;
  };

  if (loading) return (
    <div className="mn-root">
      <Navbar />
      <div className="mn-loading">
        <Loader2 size={36} className="mn-spin" />
        <p style={{ fontWeight:600, color:'var(--muted)' }}>Loading stock…</p>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  return (
    <div className="mn-root">
      <Navbar />
      <div className="mn-main">

        {/* ── Category Sidebar ── */}
        <aside className="mn-cat-sidebar">
          {STOCK_CATEGORIES.map(cat => (
            <button key={cat.id}
              className={`mn-cat-btn ${activeCat === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCat(cat.id)}>
              <span className="mn-cat-icon">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </aside>

        {/* ── Main Content ── */}
        <div className="mn-content">

          {/* ── Summary stat bar ── */}
          <div style={{ display:'flex', gap:10, padding:'12px 20px 0', flexWrap:'wrap' }}>
            {[
              { label:'Total Items',  val: stats.total,                          icon:<Package size={14}/>,     color:'#6366f1' },
              { label:'Low Stock',    val: stats.low,                            icon:<AlertTriangle size={14}/>,color:'#f59e0b' },
              { label:'Out of Stock', val: stats.out,                            icon:<X size={14}/>,            color:'#ef4444' },
              { label:'Stock Value',  val:`₹${stats.value.toLocaleString('en-IN',{maximumFractionDigits:0})}`, icon:<BarChart2 size={14}/>, color:'#22c55e' },
            ].map(s => (
              <div key={s.label} style={{
                display:'flex', alignItems:'center', gap:8,
                background:'var(--surface)', border:'1px solid var(--border)',
                borderRadius:10, padding:'8px 14px', flex:'1', minWidth:120,
              }}>
                <div style={{ color:s.color }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize:16, fontWeight:800, color:'var(--ink)', lineHeight:1 }}>{s.val}</div>
                  <div style={{ fontSize:10, color:'var(--muted)', fontWeight:600, marginTop:2 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="mn-toolbar" style={{ marginTop:8 }}>
            <div className="mn-toolbar-left">
              <h2 className="mn-page-title">
                {STOCK_CATEGORIES.find(c => c.id === activeCat)?.icon}{' '}
                {activeCat === 'all' ? 'All Stock' : STOCK_CATEGORIES.find(c => c.id === activeCat)?.name}
                {' '}<span>Items</span>
              </h2>
              <span className="mn-count-pill">{filtered.length} items</span>
              {stats.low > 0 && (
                <span style={{ fontSize:11, fontWeight:700, color:'#b45309',
                  background:'rgba(245,158,11,0.10)', padding:'3px 10px', borderRadius:100,
                  border:'1px solid rgba(245,158,11,0.25)' }}>
                  ⚠ {stats.low} low stock
                </span>
              )}
              {stats.out > 0 && (
                <span style={{ fontSize:11, fontWeight:700, color:'#dc2626',
                  background:'rgba(239,68,68,0.10)', padding:'3px 10px', borderRadius:100,
                  border:'1px solid rgba(239,68,68,0.25)' }}>
                  ✕ {stats.out} out of stock
                </span>
              )}
            </div>
            <div className="mn-toolbar-right">
              {/* Status filter */}
              <div style={{ display:'flex', gap:4, background:'var(--surface)',
                border:'1px solid var(--border)', borderRadius:8, padding:3 }}>
                {[['all','All'],['low','Low'],['out','Out']].map(([id,lbl]) => (
                  <button key={id} onClick={() => setFilterStatus(id)}
                    style={{ fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:6,
                      background: filterStatus === id ? 'var(--tomato)' : 'transparent',
                      color: filterStatus === id ? '#fff' : 'var(--muted)',
                      border:'none', cursor:'pointer' }}>
                    {lbl}
                  </button>
                ))}
              </div>

              <div className="mn-search">
                <Search size={14} />
                <input placeholder="Search items or supplier…"
                  value={search} onChange={e => setSearch(e.target.value)} />
                {search && (
                  <button style={{ background:'none', border:'none', cursor:'pointer',
                    color:'var(--muted)', display:'flex', alignItems:'center' }}
                    onClick={() => setSearch('')}><X size={13} /></button>
                )}
              </div>

              <div className="mn-view-toggle">
                <button className={`mn-view-btn ${viewMode==='grid'?'active':''}`} onClick={()=>setViewMode('grid')}><LayoutGrid size={15}/></button>
                <button className={`mn-view-btn ${viewMode==='list'?'active':''}`} onClick={()=>setViewMode('list')}><List size={15}/></button>
              </div>

              <button className="mn-new-btn" onClick={openCreate}>
                <Plus size={16}/> New Item
              </button>
            </div>
          </div>

          {/* Sort bar */}
          <div className="mn-sort-bar">
            <Filter size={13} style={{ color:'var(--muted)', flexShrink:0 }}/>
            {SORT_OPTIONS.map(s => (
              <button key={s.id}
                className={`mn-sort-chip ${sortBy===s.id?'active':''}`}
                onClick={()=>setSortBy(s.id)}>{s.label}</button>
            ))}
            <div className="mn-sort-divider"/>
            <button className="mn-sort-chip"
              onClick={()=>{ setSearch(''); setActiveCat('all'); setSortBy('default'); setFilterStatus('all'); }}>
              <X size={10}/> Clear
            </button>
          </div>

          {/* Grid */}
          <div className="mn-grid-wrap">
            <div className={`mn-grid ${viewMode==='list'?'list-view':''}`}>
              {filtered.length === 0 ? (
                <div className="mn-empty">
                  <div className="mn-empty-icon">📦</div>
                  <p>No stock items found</p>
                  <span>Try adjusting filters or add a new item</span>
                  <button className="mn-new-btn" style={{ marginTop:8 }} onClick={openCreate}>
                    <Plus size={14}/> Add First Item
                  </button>
                </div>
              ) : filtered.map(item => (
                <StockCard
                  key={item.id}
                  item={item}
                  isSelected={selectedItem?.id === item.id}
                  viewMode={viewMode}
                  onEdit={e=>{ e.stopPropagation(); openEdit(item); }}
                  onDelete={e=>{ e.stopPropagation(); handleDelete(item); }}
                  onAdjust={e=>{ e.stopPropagation(); openAdjust(item); }}
                  onHistory={e=>{ e.stopPropagation(); openHistory(item); }}
                  onToggleActive={e=>handleToggleActive(item, e)}
                  onClick={()=>openEdit(item)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════
            RIGHT PANEL
        ═══════════════════════════════════════ */}
        <aside className={`mn-panel ${panel?'open':''}`}>
          <div className="mn-panel-inner">

            {/* ── CREATE / EDIT ── */}
            {(panel === 'create' || panel === 'edit') && (
              <>
                <div className="mn-panel-header">
                  <div className="mn-panel-title">
                    {panel==='create' ? 'New Stock Item' : `Edit · ${selectedItem?.name}`}
                  </div>
                  <button className="mn-panel-close" onClick={closePanel}><X size={15}/></button>
                </div>

                <div className="mn-panel-tabs">
                  {[{id:'info',label:'Info'},{id:'supplier',label:'Supplier'},{id:'details',label:'Details'}].map(t=>(
                    <button key={t.id}
                      className={`mn-panel-tab ${panelTab===t.id?'active':''}`}
                      onClick={()=>setPanelTab(t.id)}>{t.label}</button>
                  ))}
                </div>

                <div className="mn-panel-body">
                  <div className="mn-form">

                    {/* ── INFO TAB ── */}
                    {panelTab === 'info' && (
                      <>
                        {/* Image */}
                        <div className="mn-form-group">
                          <label className="mn-form-label">Item Image</label>
                          {form.image_preview ? (
                            <div style={{ position:'relative', borderRadius:'var(--r-md)', overflow:'hidden', height:110, marginBottom:8 }}>
                              <img src={form.image_preview} alt="preview"
                                style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={clearImage}/>
                              {form.image_file && (
                                <div style={{ position:'absolute', bottom:0, left:0, right:0,
                                  background:'rgba(0,0,0,0.55)', padding:'4px 10px',
                                  fontSize:10, color:'#fff', fontWeight:600,
                                  display:'flex', alignItems:'center', gap:5 }}>
                                  <ImageIcon size={10}/>{form.image_file.name}
                                  <span style={{ marginLeft:'auto', opacity:0.7 }}>
                                    {(form.image_file.size/1024).toFixed(0)} KB
                                  </span>
                                </div>
                              )}
                              <button onClick={clearImage} style={{
                                position:'absolute', top:8, right:8, width:26, height:26,
                                borderRadius:8, background:'rgba(26,15,10,0.65)', border:'none',
                                color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <X size={12}/>
                              </button>
                            </div>
                          ) : (
                            <div className="mn-img-upload"
                              onClick={()=>fileInputRef.current?.click()}
                              onDragOver={e=>e.preventDefault()}
                              onDrop={e=>{ e.preventDefault(); handleImageFileChange({target:{files:[e.dataTransfer.files[0]]}}) }}
                              style={{ cursor:'pointer' }}>
                              <div className="mn-img-upload-icon"><Upload size={20}/></div>
                              <p><strong>Click to browse</strong> or drag & drop<br/>
                                <span style={{ fontSize:11, color:'var(--muted)' }}>JPG, PNG, WebP — max 5 MB</span>
                              </p>
                            </div>
                          )}
                          <input ref={fileInputRef} type="file" accept="image/*"
                            style={{ display:'none' }} onChange={handleImageFileChange}/>
                          <div style={{ display:'flex', alignItems:'center', gap:6, margin:'6px 0 4px' }}>
                            <div style={{ flex:1, height:1, background:'var(--border)' }}/>
                            <span style={{ fontSize:10, color:'var(--muted)', fontWeight:600 }}>OR PASTE URL</span>
                            <div style={{ flex:1, height:1, background:'var(--border)' }}/>
                          </div>
                          <input className="mn-form-input" placeholder="https://…"
                            value={form.image_url} onChange={e=>handleImageUrlChange(e.target.value)}/>
                          {saving && form.image_file && uploadProgress > 0 && uploadProgress < 100 && (
                            <div style={{ marginTop:6 }}>
                              <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--muted)', marginBottom:3 }}>
                                <span>Uploading…</span><span>{uploadProgress}%</span>
                              </div>
                              <div style={{ height:4, background:'var(--border)', borderRadius:4 }}>
                                <div style={{ height:'100%', borderRadius:4,
                                  background:'linear-gradient(90deg,var(--tomato),var(--saffron))',
                                  width:`${uploadProgress}%`, transition:'width 0.2s' }}/>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Name */}
                        <div className="mn-form-group">
                          <label className="mn-form-label">Item Name *</label>
                          <input className="mn-form-input" placeholder="e.g. Basmati Rice"
                            value={form.name} onChange={e=>setF('name',e.target.value)}/>
                        </div>

                        {/* Category + Unit */}
                        <div className="mn-form-row">
                          <div className="mn-form-group">
                            <label className="mn-form-label">Category</label>
                            <select className="mn-form-select" value={form.category} onChange={e=>setF('category',e.target.value)}>
                              <option value="">— Select —</option>
                              {STOCK_CATEGORIES.filter(c=>c.id!=='all').map(c=>(
                                <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="mn-form-group">
                            <label className="mn-form-label">Unit *</label>
                            <select className="mn-form-select" value={form.unit} onChange={e=>setF('unit',e.target.value)}>
                              {STOCK_UNITS.map(u=>(
                                <option key={u.id} value={u.id}>{u.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Quantities */}
                        <div className="mn-form-group">
                          <label className="mn-form-label">Stock Quantities</label>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                            {[
                              { key:'current_qty', label:'Current', placeholder:'0', color:'#22c55e' },
                              { key:'min_qty',     label:'Min (Alert)', placeholder:'0', color:'#f59e0b' },
                              { key:'max_qty',     label:'Max Capacity', placeholder:'0', color:'#6366f1' },
                            ].map(f=>(
                              <div key={f.key}>
                                <div style={{ fontSize:10, fontWeight:700, color:f.color,
                                  textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:4 }}>
                                  {f.label}
                                </div>
                                <input className="mn-form-input" type="number" min="0" step="0.01"
                                  placeholder={f.placeholder}
                                  value={form[f.key]}
                                  onChange={e=>setF(f.key,e.target.value)}
                                  style={{ textAlign:'center' }}/>
                              </div>
                            ))}
                          </div>
                          {form.current_qty !== '' && form.min_qty !== '' && (
                            <div style={{ marginTop:6, fontSize:11, color:'var(--muted)' }}>
                              Status: <strong style={{ color: getStockStatus({
                                current_qty: form.current_qty, min_qty: form.min_qty
                              }).color }}>
                                {getStockStatus({ current_qty: form.current_qty, min_qty: form.min_qty }).label}
                              </strong>
                            </div>
                          )}
                        </div>

                        {/* Cost per unit */}
                        <div className="mn-form-group">
                          <label className="mn-form-label">Cost per {STOCK_UNITS.find(u=>u.id===form.unit)?.label || 'unit'} (₹)</label>
                          <input className="mn-form-input" type="number" min="0" step="0.01"
                            placeholder="e.g. 45.00"
                            value={form.cost_per_unit}
                            onChange={e=>setF('cost_per_unit',e.target.value)}/>
                          {form.current_qty && form.cost_per_unit && (
                            <div style={{ fontSize:11, color:'var(--muted)', marginTop:4 }}>
                              Total value: <strong style={{ color:'var(--tomato)' }}>
                                ₹{(parseFloat(form.current_qty)*parseFloat(form.cost_per_unit)).toFixed(2)}
                              </strong>
                            </div>
                          )}
                        </div>

                        {/* Active toggle */}
                        <div className="mn-avail-toggle">
                          <div className="mn-avail-toggle-left">
                            <span className="mn-avail-toggle-label">Item Active</span>
                            <span className="mn-avail-toggle-sub">
                              {form.is_active ? 'Tracked in stock' : 'Archived / inactive'}
                            </span>
                          </div>
                          <label className="mn-toggle-switch">
                            <input type="checkbox" checked={form.is_active}
                              onChange={e=>setF('is_active',e.target.checked)}/>
                            <span className="mn-toggle-track"/>
                          </label>
                        </div>
                      </>
                    )}

                    {/* ── SUPPLIER TAB ── */}
                    {panelTab === 'supplier' && (
                      <>
                        <div className="mn-form-group">
                          <label className="mn-form-label">Supplier Name</label>
                          <input className="mn-form-input" placeholder="e.g. Fresh Farms Ltd."
                            value={form.supplier_name} onChange={e=>setF('supplier_name',e.target.value)}/>
                        </div>
                        <div className="mn-form-group">
                          <label className="mn-form-label">Supplier Contact</label>
                          <input className="mn-form-input" placeholder="+91 98765 43210"
                            value={form.supplier_contact} onChange={e=>setF('supplier_contact',e.target.value)}/>
                        </div>
                        <div className="mn-form-group">
                          <label className="mn-form-label">Expiry / Best Before Date</label>
                          <input className="mn-form-input" type="date"
                            value={form.expiry_date} onChange={e=>setF('expiry_date',e.target.value)}/>
                          {form.expiry_date && (() => {
                            const w = expiryWarning(form.expiry_date);
                            return w ? (
                              <div style={{ marginTop:4, fontSize:11, color:w.color, fontWeight:700 }}>
                                ⚠ {w.label}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </>
                    )}

                    {/* ── DETAILS TAB ── */}
                    {panelTab === 'details' && (
                      <div className="mn-form-group">
                        <label className="mn-form-label">Notes / Storage Instructions</label>
                        <textarea className="mn-form-textarea"
                          placeholder="e.g. Store in cool dry place, refrigerate after opening…"
                          rows={5}
                          value={form.notes} onChange={e=>setF('notes',e.target.value)}/>
                      </div>
                    )}

                    <button className="mn-btn-save" onClick={handleSave} disabled={saving}>
                      {saving
                        ? <><Loader2 size={15} className="mn-spin"/>
                            {form.image_file && uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Saving…'}
                          </>
                        : <><CheckCircle2 size={15}/>
                            {panel==='create' ? 'Add to Stock' : 'Save Changes'}
                          </>}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── ADJUST STOCK ── */}
            {panel === 'adjust' && selectedItem && (
              <>
                <div className="mn-panel-header">
                  <div className="mn-panel-title">Adjust · {selectedItem.name}</div>
                  <button className="mn-panel-close" onClick={closePanel}><X size={15}/></button>
                </div>

                <div className="mn-panel-body">
                  <div className="mn-form">
                    {/* Current stock info */}
                    <div style={{ background:'var(--surface)', border:'1px solid var(--border)',
                      borderRadius:12, padding:'12px 16px', marginBottom:16 }}>
                      <div style={{ fontSize:11, color:'var(--muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:8 }}>
                        Current Stock
                      </div>
                      <div style={{ display:'flex', alignItems:'baseline', gap:6 }}>
                        <span style={{ fontSize:28, fontWeight:800, color:'var(--ink)', lineHeight:1 }}>
                          {selectedItem.current_qty}
                        </span>
                        <span style={{ fontSize:14, color:'var(--muted)', fontWeight:600 }}>
                          {selectedItem.unit}
                        </span>
                      </div>
                      {(() => {
                        const s = getStockStatus(selectedItem);
                        return (
                          <div style={{ marginTop:4, display:'inline-flex', alignItems:'center', gap:5,
                            background:s.bg, border:`1px solid ${s.dot}30`, borderRadius:100,
                            padding:'2px 10px', fontSize:11, fontWeight:700, color:s.color }}>
                            <span style={{ width:6, height:6, borderRadius:'50%', background:s.dot, display:'inline-block' }}/>
                            {s.label}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Adjustment type */}
                    <div className="mn-form-group">
                      <label className="mn-form-label">Adjustment Type</label>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                        {ADJUST_TYPES.map(t=>(
                          <button key={t.id}
                            onClick={()=>setAF('type',t.id)}
                            style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px',
                              borderRadius:10, border:`1.5px solid ${adjustForm.type===t.id ? t.color : 'var(--border)'}`,
                              background: adjustForm.type===t.id ? `${t.color}15` : 'var(--surface)',
                              cursor:'pointer', color: adjustForm.type===t.id ? t.color : 'var(--muted)',
                              fontWeight:700, fontSize:12 }}>
                            <span style={{ color:t.color }}>{t.icon}</span>
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Qty */}
                    <div className="mn-form-group">
                      <label className="mn-form-label">
                        Quantity ({selectedItem.unit}) *
                      </label>
                      <input className="mn-form-input" type="number" min="0.01" step="0.01"
                        placeholder="0.00"
                        value={adjustForm.qty}
                        onChange={e=>setAF('qty',e.target.value)}/>
                      {adjustForm.qty && adjustForm.type && (() => {
                        const q   = parseFloat(adjustForm.qty) || 0;
                        const cur = selectedItem.current_qty;
                        const isDeduct = ['use','waste','transfer'].includes(adjustForm.type);
                        const newQty = isDeduct ? cur - q : cur + q;
                        return (
                          <div style={{ fontSize:11, color:'var(--muted)', marginTop:4 }}>
                            New quantity:{' '}
                            <strong style={{ color: newQty < 0 ? '#ef4444' : 'var(--tomato)' }}>
                              {Math.max(0, newQty).toFixed(2)} {selectedItem.unit}
                            </strong>
                            {newQty < 0 && <span style={{ color:'#ef4444' }}> (will cap at 0)</span>}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Cost per unit (optional, for restock) */}
                    {adjustForm.type === 'restock' && (
                      <div className="mn-form-group">
                        <label className="mn-form-label">Cost per {selectedItem.unit} (₹) — optional</label>
                        <input className="mn-form-input" type="number" min="0" step="0.01"
                          placeholder="Update cost if changed"
                          value={adjustForm.cost_per_unit}
                          onChange={e=>setAF('cost_per_unit',e.target.value)}/>
                      </div>
                    )}

                    {/* Note */}
                    <div className="mn-form-group">
                      <label className="mn-form-label">Note / Reason</label>
                      <textarea className="mn-form-textarea" rows={3}
                        placeholder="e.g. Weekly delivery from Fresh Farms, expired batch disposed…"
                        value={adjustForm.note}
                        onChange={e=>setAF('note',e.target.value)}/>
                    </div>

                    <button className="mn-btn-save" onClick={handleAdjust} disabled={saving}>
                      {saving
                        ? <><Loader2 size={15} className="mn-spin"/> Saving…</>
                        : <><CheckCircle2 size={15}/> Record Adjustment</>}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── HISTORY ── */}
            {panel === 'history' && selectedItem && (
              <>
                <div className="mn-panel-header">
                  <div className="mn-panel-title">History · {selectedItem.name}</div>
                  <button className="mn-panel-close" onClick={closePanel}><X size={15}/></button>
                </div>
                <div className="mn-panel-body">
                  {histLoading ? (
                    <div style={{ display:'flex', justifyContent:'center', padding:32 }}>
                      <Loader2 size={24} className="mn-spin"/>
                    </div>
                  ) : history.length === 0 ? (
                    <div style={{ textAlign:'center', padding:32, color:'var(--muted)' }}>
                      <Clock size={32} style={{ marginBottom:8, opacity:0.4 }}/>
                      <p style={{ fontWeight:600 }}>No adjustment history yet</p>
                    </div>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {history.map((h, i) => {
                        const t = ADJUST_TYPES.find(x => x.id === h.type) || ADJUST_TYPES[0];
                        const isDeduct = ['use','waste','transfer'].includes(h.type);
                        return (
                          <div key={h.id || i} style={{ background:'var(--surface)',
                            border:'1px solid var(--border)', borderRadius:12, padding:'12px 14px' }}>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                <span style={{ color:t.color }}>{t.icon}</span>
                                <span style={{ fontSize:12, fontWeight:700, color:t.color }}>{t.label}</span>
                              </div>
                              <span style={{ fontSize:12, fontWeight:800,
                                color: isDeduct ? '#ef4444' : '#22c55e' }}>
                                {isDeduct ? '−' : '+'}{h.qty} {selectedItem.unit}
                              </span>
                            </div>
                            {h.note && <p style={{ fontSize:12, color:'var(--text)', marginBottom:4 }}>{h.note}</p>}
                            <div style={{ fontSize:10, color:'var(--muted)' }}>
                              {new Date(h.created_at).toLocaleString('en-IN')}
                              {h.staff_name && ` · ${h.staff_name}`}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {!panel && (
              <div style={{ flex:1, display:'flex', flexDirection:'column',
                alignItems:'center', justifyContent:'center', gap:12,
                color:'var(--light-muted)', textAlign:'center', padding:24 }}>
                <div style={{ fontSize:40 }}>📦</div>
                <p style={{ fontWeight:600, fontSize:13 }}>Select an item or add new stock</p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Toast */}
      <div className={`mn-toast ${toast.show?'show':''}`}>
        <CheckCircle2 size={14} className="mn-toast-icon"/>
        {toast.msg}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   STOCK CARD
═══════════════════════════════════════ */
function StockCard({ item, isSelected, viewMode, onClick, onEdit, onDelete, onAdjust, onHistory, onToggleActive }) {
  const imgSrc  = item.image_url ? `${imgurl}${item.image_url}` : null;
  const catInfo = STOCK_CATEGORIES.find(c => c.id === item.category) || STOCK_CATEGORIES[STOCK_CATEGORIES.length-1];
  const status  = getStockStatus(item);
  const expWarn = (() => {
    if (!item.expiry_date) return null;
    const days = Math.ceil((new Date(item.expiry_date) - new Date()) / 86400000);
    if (days < 0)  return { label:'Expired',      color:'#ef4444' };
    if (days <= 7) return { label:`Exp in ${days}d`, color: days <= 3 ? '#ef4444' : '#f59e0b' };
    return null;
  })();

  const pctFull = item.max_qty > 0
    ? Math.min(100, Math.round((item.current_qty / item.max_qty) * 100))
    : null;

  if (viewMode === 'list') {
    return (
      <div className={`mn-card list-view-card ${isSelected?'selected':''} ${!item.is_active?'unavailable':''}`}
        onClick={onClick}>
        <div className="mn-card-img">
          {imgSrc
            ? <img src={imgSrc} alt={item.name}/>
            : <div className="mn-card-img-placeholder">{catInfo.icon}</div>}
          <span className={`mn-avail-dot ${item.is_active?'on':'off'}`}/>
        </div>
        <div className="mn-card-body">
          <div className="mn-card-top">
            <div className="mn-card-name" style={{ fontSize:14 }}>{item.name}</div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:5,
              background:status.bg, borderRadius:100, padding:'2px 10px',
              fontSize:10, fontWeight:700, color:status.color }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:status.dot, display:'inline-block' }}/>
              {status.label}
            </div>
          </div>
          <div className="mn-card-meta" style={{ borderTop:'none', paddingTop:0, gap:10 }}>
            <span className="mn-card-kcal"><Package size={10}/> {item.current_qty} {item.unit}</span>
            {item.supplier_name && <span className="mn-card-kcal"><Truck size={10}/> {item.supplier_name}</span>}
            {expWarn && <span style={{ fontSize:10, fontWeight:700, color:expWarn.color }}>⚠ {expWarn.label}</span>}
          </div>
          <div style={{ marginLeft:'auto', textAlign:'right' }}>
            <div className="mn-card-price" style={{ fontSize:13 }}>₹{item.cost_per_unit}/{item.unit}</div>
            <div style={{ fontSize:10, color:'var(--muted)', fontWeight:600 }}>
              Val: ₹{(item.current_qty * item.cost_per_unit).toFixed(0)}
            </div>
          </div>
          <div className="mn-card-actions">
            <button className="mn-card-action-btn" title="Adjust Stock" onClick={onAdjust}><RefreshCw size={12}/></button>
            <button className="mn-card-action-btn" title="History" onClick={onHistory}><Clock size={12}/></button>
            <button className="mn-card-action-btn" title="Edit" onClick={onEdit}><Edit3 size={12}/></button>
            <button className="mn-card-action-btn" title={item.is_active?'Deactivate':'Activate'} onClick={onToggleActive}>
              {item.is_active ? <EyeOff size={12}/> : <Eye size={12}/>}
            </button>
            <button className="mn-card-action-btn danger" title="Delete" onClick={onDelete}><Trash2 size={12}/></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mn-card ${isSelected?'selected':''} ${!item.is_active?'unavailable':''}`}
      onClick={onClick}>
      <div className="mn-card-img">
        {imgSrc
          ? <img src={imgSrc} alt={item.name}/>
          : <div className="mn-card-img-placeholder">{catInfo.icon}</div>}

        {/* Status badge */}
        <div style={{ position:'absolute', top:8, right:8,
          background:status.bg, border:`1px solid ${status.dot}40`,
          borderRadius:100, padding:'2px 8px',
          fontSize:9, fontWeight:800, color:status.color,
          backdropFilter:'blur(4px)' }}>
          {status.label}
        </div>

        {/* Expiry warning */}
        {expWarn && (
          <div style={{ position:'absolute', top:8, left:8,
            background:'rgba(239,68,68,0.90)', color:'#fff',
            fontSize:9, fontWeight:800, padding:'2px 7px',
            borderRadius:100, letterSpacing:'0.3px' }}>
            ⚠ {expWarn.label}
          </div>
        )}

        <span className={`mn-avail-dot ${item.is_active?'on':'off'}`}/>
      </div>

      <div className="mn-card-body">
        <div className="mn-card-top">
          <div className="mn-card-name">{item.name}</div>
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <div className="mn-card-price" style={{ fontSize:13 }}>
              {item.current_qty} <span style={{ fontSize:10, fontWeight:600, color:'var(--muted)' }}>{item.unit}</span>
            </div>
            <div style={{ fontSize:10, color:'var(--muted)', fontWeight:600, marginTop:1 }}>
              min: {item.min_qty} {item.unit}
            </div>
          </div>
        </div>

        {/* Stock level bar */}
        {pctFull !== null && (
          <div style={{ marginBottom:6 }}>
            <div style={{ height:4, background:'var(--border)', borderRadius:4, overflow:'hidden' }}>
              <div style={{
                height:'100%', borderRadius:4,
                background: pctFull <= 20 ? '#ef4444' : pctFull <= 50 ? '#f59e0b' : '#22c55e',
                width:`${pctFull}%`, transition:'width 0.4s'
              }}/>
            </div>
            <div style={{ fontSize:9, color:'var(--muted)', marginTop:2, textAlign:'right' }}>{pctFull}% full</div>
          </div>
        )}

        <p className="mn-card-desc" style={{ fontSize:11 }}>
          {item.supplier_name
            ? <><Truck size={10} style={{ display:'inline', verticalAlign:'middle', marginRight:3 }}/>{item.supplier_name}</>
            : 'No supplier info'}
          {item.cost_per_unit > 0 && (
            <span style={{ float:'right', fontWeight:700, color:'var(--tomato)' }}>
              ₹{item.cost_per_unit}/{item.unit}
            </span>
          )}
        </p>

        <div className="mn-card-meta">
          <span className="mn-card-kcal" style={{ fontSize:10 }}>
            Val: <strong style={{ color:'var(--tomato)' }}>
              ₹{(item.current_qty * item.cost_per_unit).toFixed(0)}
            </strong>
          </span>
          <div className="mn-card-actions" onClick={e=>e.stopPropagation()}>
            <button className="mn-card-action-btn" title="Adjust" onClick={onAdjust}><RefreshCw size={12}/></button>
            <button className="mn-card-action-btn" title="History" onClick={onHistory}><Clock size={12}/></button>
            <button className="mn-card-action-btn" title="Edit" onClick={onEdit}><Edit3 size={12}/></button>
            <button className="mn-card-action-btn" title={item.is_active?'Deactivate':'Activate'} onClick={onToggleActive}>
              {item.is_active ? <EyeOff size={12}/> : <Eye size={12}/>}
            </button>
            <button className="mn-card-action-btn danger" title="Delete" onClick={onDelete}><Trash2 size={12}/></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Stock;
